import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, AlertCircle, Loader2, CheckCircle2, Upload, Download, MapPin, Plus, Trash2, Mail, RefreshCw, Search, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ReactPlayer from 'react-player';

interface BillingConfig {
  cuit: string;
  nombre: string;
  condicion_iva: string;
  condicion_iibb: string;
  numero_iibb: string;
  fecha_inicio_actividades: string;
  provincia: string;
  localidad: string;
  domicilio: string;
  codigo_postal: string;
  config_lista?: boolean;
}

interface Local {
  id: number;
  nombre: string;
  direccion: string;
  tipo: 'PoS' | 'Centro de Distribución';
  punto_venta?: number;
}

const PROVINCIAS = [
  'Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
  'Ciudad Autónoma de Buenos Aires'
];

const CONDICIONES_IVA = [
  'Responsable Inscripto',
  'Monotributista',
  'Exento',
  'Responsable No Inscripto'
];

const CONDICIONES_IIBB = [
  'Regimen Simplificado',
  'Regimen General',
  'Convenio Multilateral',
  'Exento'
];

export const Billing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('edit') === 'true';
  const [currentStep, setCurrentStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [validatingCertificate, setValidatingCertificate] = useState(false);
  const [certificateValidated, setCertificateValidated] = useState(false);
  const [locales, setLocales] = useState<Local[]>([]);
  const [billingConfig, setBillingConfig] = useState<BillingConfig>({
    cuit: '',
    nombre: '',
    condicion_iva: '',
    condicion_iibb: '',
    numero_iibb: '',
    fecha_inicio_actividades: '',
    provincia: '',
    localidad: '',
    domicilio: '',
    codigo_postal: '',
    config_lista: false
  });

  useEffect(() => {
    fetchBillingConfig();
    fetchLocales();
  }, []);
  
  // Reset to first step if in edit mode
  useEffect(() => {
    if (isEditMode && billingConfig.cuit) {
      setCurrentStep(0);
      setShowForm(true);
    }
  }, [isEditMode, billingConfig]);

  const fetchBillingConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: config, error: configError } = await supabase
        .from('config_facturacion')
        .select('*')
        .eq('id_company', profile.id_company)
        .single();

      if (configError && configError.code !== 'PGRST116') throw configError;
      
      if (config) {
        setBillingConfig(config);
        setShowForm(true);
        
        // If in edit mode, always go to the first step
        if (isEditMode) {
          setCurrentStep(0);
        }
        // If config is already complete and not in edit mode, set the current step accordingly
        else if (config.config_lista) {
          setCurrentStep(3); // Go to the last step
        } else {
          // Otherwise determine the step based on what's been configured
          setCurrentStep(1);
        }
      }
    } catch (err) {
      console.error('Error fetching billing config:', err);
      setError(err instanceof Error ? err.message : 'Error loading billing configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocales = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: localesData, error: localesError } = await supabase
        .from('locales')
        .select('*')
        .eq('id_company', profile.id_company)
        .eq('tipo', 'PoS');

      if (localesError) throw localesError;
      setLocales(localesData || []);
    } catch (err) {
      console.error('Error fetching locales:', err);
      setError(err instanceof Error ? err.message : 'Error loading locations');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { error } = await supabase
        .from('config_facturacion')
        .upsert({
          id_company: profile.id_company,
          ...billingConfig,
          config_lista: false // Ensure config_lista is false until fully configured
        });

      if (error) throw error;

      setSuccess('Configuración de facturación guardada correctamente');
      setCurrentStep(1);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving billing config:', err);
      setError(err instanceof Error ? err.message : 'Error saving billing configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGetCertificateRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/cc354da3-72c7-49ec-816e-51e81d1171d8', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          mode: 'get_csr'
        }),
      });

      const data = await response.json();

      if (data.response === 'success') {
        const blob = new Blob([data.data], { type: 'application/x-x509-ca-cert' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'SolicitudCertificado.csr';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Failed to get certificate request');
      }
    } catch (err) {
      console.error('Error getting certificate request:', err);
      setError(err instanceof Error ? err.message : 'Error getting certificate request');
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateUpload = async () => {
    if (!certificateFile) return;

    try {
      setValidatingCertificate(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const fileContent = await certificateFile.text();

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/cc354da3-72c7-49ec-816e-51e81d1171d8', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          mode: 'push_crt',
          certificate: fileContent
        }),
      });

      const data = await response.json();

      if (data.response === 'success') {
        setCertificateValidated(true);
        setSuccess('Certificado validado correctamente');
        setTimeout(() => {
          setCurrentStep(2);
          setSuccess(null);
        }, 2000);
      } else {
        throw new Error('Certificate validation failed');
      }
    } catch (err) {
      console.error('Error validating certificate:', err);
      setError(err instanceof Error ? err.message : 'Error validating certificate');
    } finally {
      setValidatingCertificate(false);
    }
  };

  const handlePuntoVentaChange = (localId: number, value: string) => {
    const numValue = value === '' ? undefined : parseInt(value);
    setLocales(prev => prev.map(local => 
      local.id === localId ? { ...local, punto_venta: numValue } : local
    ));
  };

  const handleFinishSetup = async () => {
    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      // First update the locales with punto_venta values if needed
      for (const local of locales) {
        if (local.punto_venta) {
          const { error: localError } = await supabase
            .from('locales')
            .update({ punto_venta: local.punto_venta })
            .eq('id', local.id)
            .eq('id_company', profile.id_company);
            
          if (localError) throw localError;
        }
      }
      
      // Then update the config_facturacion to mark it as complete
      const { error: updateError } = await supabase
        .from('config_facturacion')
        .update({ config_lista: true })
        .eq('id_company', profile.id_company);

      if (updateError) throw updateError;

      setSuccess('Configuración completada exitosamente');
      setTimeout(() => {
        setSuccess(null);
        // Navigate to the configuration page
        navigate('/configuracion');
      }, 2000);
    } catch (err) {
      console.error('Error finishing setup:', err);
      setError(err instanceof Error ? err.message : 'Error completing configuration');
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    'Datos fiscales',
    'Certificado digital',
    'Computador fiscal',
    'Puntos de venta'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-8 h-8 text-gray-700" />
          <h1 className="text-2xl font-semibold text-gray-900">Facturación</h1>
        </div>

        <div className="relative">
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
            <div
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-500"
            />
          </div>
          <div className="flex justify-between">
            {steps.map((step, index) => (
              <div
                key={step}
                className={`flex flex-col items-center ${
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                }`}
                style={{ width: '25%' }}
              >
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full mb-2 ${
                    index < currentStep
                      ? 'bg-blue-500 text-white'
                      : index === currentStep
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-500'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {index < currentStep ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="text-sm text-center">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-600">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {currentStep === 0 && !showForm && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <CreditCard className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            Configurar Facturación
          </h2>
          <p className="text-gray-600 mb-6">
            Configure los datos necesarios para la facturación electrónica
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Comenzar configuración
          </button>
        </div>
      )}

      {currentStep === 0 && showForm && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Datos Fiscales</h2>
            <p className="text-sm text-gray-500">
              Complete los datos fiscales de su empresa
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CUIT
                </label>
                <input
                  type="text"
                  value={billingConfig.cuit}
                  onChange={(e) => setBillingConfig({ ...billingConfig, cuit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                  pattern="[0-9]{11}"
                  title="El CUIT debe contener 11 números"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Razón Social
                </label>
                <input
                  type="text"
                  value={billingConfig.nombre}
                  onChange={(e) => setBillingConfig({ ...billingConfig, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condición IVA
                </label>
                <select
                  value={billingConfig.condicion_iva}
                  onChange={(e) => setBillingConfig({ ...billingConfig, condicion_iva: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Seleccionar condición</option>
                  {CONDICIONES_IVA.map(condicion => (
                    <option key={condicion} value={condicion}>
                      {condicion}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condición IIBB
                </label>
                <select
                  value={billingConfig.condicion_iibb}
                  onChange={(e) => setBillingConfig({ ...billingConfig, condicion_iibb: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Seleccionar condición</option>
                  {CONDICIONES_IIBB.map(condicion => (
                    <option key={condicion} value={condicion}>
                      {condicion}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número IIBB
                </label>
                <input
                  type="text"
                  value={billingConfig.numero_iibb}
                  onChange={(e) => setBillingConfig({ ...billingConfig, numero_iibb: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha Inicio Actividades
                </label>
                <input
                  type="date"
                  value={billingConfig.fecha_inicio_actividades}
                  onChange={(e) => setBillingConfig({ ...billingConfig, fecha_inicio_actividades: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincia
                </label>
                <select
                  value={billingConfig.provincia}
                  onChange={(e) => setBillingConfig({ ...billingConfig, provincia: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Seleccionar provincia</option>
                  {PROVINCIAS.map(provincia => (
                    <option key={provincia} value={provincia}>
                      {provincia}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Localidad
                </label>
                <input
                  type="text"
                  value={billingConfig.localidad}
                  onChange={(e) => setBillingConfig({ ...billingConfig, localidad: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domicilio
                </label>
                <input
                  type="text"
                  value={billingConfig.domicilio}
                  onChange={(e) => setBillingConfig({ ...billingConfig, domicilio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código Postal
                </label>
                <input
                  type="text"
                  value={billingConfig.codigo_postal}
                  onChange={(e) => setBillingConfig({ ...billingConfig, codigo_postal: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar y continuar'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {currentStep === 1 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Certificado Digital</h2>
            <p className="text-sm text-gray-500">
              Obtenga y configure su certificado digital para facturación electrónica
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex justify-center">
              <button
                onClick={handleGetCertificateRequest}
                disabled={loading}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                Obtener y descargar solicitud de certificado
              </button>
            </div>

            {!certificateValidated && (
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Una vez obtenido el certificado cargalo aqui:
                </h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="flex flex-col items-center">
                    <Upload className="w-12 h-12 text-gray-400 mb-4" />
                    <input
                      type="file"
                      accept=".crt"
                      onChange={(e) => setCertificateFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="certificate-upload"
                    />
                    <label
                      htmlFor="certificate-upload"
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer mb-2"
                    >
                      Seleccionar archivo
                    </label>
                    {certificateFile && (
                      <div className="text-sm text-gray-600 mb-4">
                        Archivo seleccionado: {certificateFile.name}
                      </div>
                    )}
                    <button
                      onClick={handleCertificateUpload}
                      disabled={!certificateFile || validatingCertificate}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {validatingCertificate ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validando...
                        </>
                      ) : (
                        'Validar certificado'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Dar de alta computador fiscal</h2>
            <p className="text-sm text-gray-500">
              Tutorial de configuración del computador fiscal
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="max-w-2xl mx-auto rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <ReactPlayer
                url="https://www.youtube.com/watch?v=7yhcyn_4cUc"
                width="100%"
                height="100%"
                controls={true}
                playing={false}
              />
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Puntos de venta</h2>
            <p className="text-sm text-gray-500">
              Configure los números de punto de venta para cada local
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locales.map(local => (
                <div key={local.id} className="bg-white border rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{local.nombre}</h3>
                  <p className="text-gray-600 mb-4">{local.direccion}</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de punto de venta
                    </label>
                    <input
                      type="number"
                      value={local.punto_venta || ''}
                      onChange={(e) => handlePuntoVentaChange(local.id, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="Ej: 0001"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Tutorial de configuración
              </h3>
              <div className="max-w-2xl mx-auto rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <ReactPlayer
                  url="https://www.youtube.com/watch?v=p24qhUFIe68&list=WL&index=4"
                  width="100%"
                  height="100%"
                  controls={true}
                  playing={false}
                />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleFinishSetup}
                disabled={saving || locales.some(local => !local.punto_venta)}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Finalizando...
                  </>
                ) : (
                  'Finalizar configuración'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};